import { z } from "zod";
import { channelDataSourceSchema } from "./channel-data-trace.js";
import { dashboardMetricAggregateSchema } from "./channels.js";
/** Records are read a page at a time, each page this many at most, so every record can be read however many there are. */
export const CHANNEL_WIDGET_RECORDS_PAGE = 2_000;
/** The widgets whose records can be listed, and whose points can be picked. */
export const channelWidgetRecordsTypes = [
    "metric", "bar", "breakdown", "time_series", "grouped_bar", "stacked_bar", "waterfall",
];
/**
 * A point of a widget, as it labels it: a bar, a period, a waterfall's step. A bar of a grouped or stacked chart may
 * name one of its pieces (`segment`), or the piece the chart folded the rest into ("Other"), as the segments it
 * showed (`otherThan`): its records are the ones in none of them.
 */
export const channelWidgetRecordsPointSchema = z.object({
    label: z.string().max(400),
    currency: z.string().max(8).optional(),
    segment: z.string().max(400).optional(),
    otherThan: z.array(z.string().max(400)).max(50).optional(),
}).strict();
/**
 * The records behind a widget's figure, on a dashboard or a channel's Overview, or behind one of its points: the
 * data set's records under the dashboard's filters, the widget's own filter and its period, and the point's category,
 * period, piece or step. They are read
 * now, as the reader, a page at a time from `offset`, largest measure first. The first page also gives `figures` (the
 * widget's measure over all of them, one per currency where amounts take their currency from each record), `total`
 * and where they come from; a later page gives only its records. A figure the tile showed earlier differs when the
 * records have changed since.
 */
export const channelWidgetRecordsSchema = z.object({
    schemaVersion: z.literal("mlx.channel-widget-records.v1"),
    channelId: z.string().trim().min(1),
    objectTypeId: z.string().trim().min(1),
    /** The dashboard the widget is on; null for a widget on the channel's Overview. */
    viewId: z.string().trim().min(1).nullable(),
    widgetId: z.string().trim().min(1),
    /** The point picked, as the widget labels it; null for the whole widget. */
    point: channelWidgetRecordsPointSchema.nullable(),
    /** The widget's own period in words, such as "Issue date: April to September 2026"; null when it has none. */
    period: z.string().max(400).nullable(),
    measure: z.object({ aggregate: dashboardMetricAggregateSchema, fieldKey: z.string().optional() }).strict(),
    figures: z.array(z.object({ currency: z.string().max(8).optional(), value: z.number() }).strict()).max(16).nullable(),
    /** The fields the records are shown with: what the widget reads, groups by and keeps to. */
    fields: z.array(z.object({ key: z.string(), label: z.string(), type: z.string() }).strict()).min(1).max(16),
    records: z.array(z.object({
        id: z.string(),
        title: z.string(),
        values: z.record(z.string(), z.unknown()),
    }).strict()).max(CHANNEL_WIDGET_RECORDS_PAGE),
    offset: z.number().int().nonnegative(),
    /** Where the next page starts; null on the last. */
    nextOffset: z.number().int().positive().nullable(),
    total: z.number().int().nonnegative().nullable(),
    /** Where the records come from, with how fresh each source is. */
    dataSources: z.array(channelDataSourceSchema).max(16).nullable(),
    readAt: z.number().int().nonnegative(),
}).strict();
